// src/pages/Withdrawal.tsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import {
  withdraw,
  getTransactions,
  getTransactionStatistics,
} from "../redux/slices/transactionSlice";
import { getWallet } from "../redux/slices/walletSlice";
import { GlobalStyles } from "../styles/globalStyles";
import {
  ArrowUpRight,
  Wallet as WalletIcon,
  AlertCircle,
  CheckCircle,
  History,
  RefreshCw,
  TrendingUp,
  Calculator,
  AlertTriangle,
  Sparkles,
  Shield,
  Lock,
} from "lucide-react";
import toast from "react-hot-toast";
import { formatFiat, formatCrypto } from "../utils/currency";
import { transactionService } from "../services/api";
import { Link } from "react-router-dom";

interface CryptoPrice {
  [key: string]: {
    price: number;
    network: string;
    crypto_id: string;
  };
}

const INITIAL_CURRENCIES = {
  BTC: { price: 0, network: "Bitcoin", crypto_id: "bitcoin" },
  ETH: { price: 0, network: "Ethereum", crypto_id: "ethereum" },
  USDT: { price: 0, network: "Ethereum", crypto_id: "tether" },
  BNB: { price: 0, network: "Binance Smart Chain", crypto_id: "binancecoin" },
  SOL: { price: 0, network: "Solana", crypto_id: "solana" },
};

interface CalculatedWithdrawal {
  usd_amount: number;
  crypto_amount: number;
  currency: string;
  crypto_price: number;
  network: string;
  meets_minimum: boolean;
  minimum_required: number;
}

const Withdrawal: React.FC = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const { wallet, isLoading: walletLoading } = useSelector(
    (state: RootState) => state.wallet,
  );
  const { transactions, statistics } = useSelector(
    (state: RootState) => state.transactions,
  );

  const [loading, setLoading] = useState(false);
  const [fetchingPrices, setFetchingPrices] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [formData, setFormData] = useState({
    usd_amount: "",
    currency: "BTC",
  });
  const [cryptoPrices, setCryptoPrices] = useState<CryptoPrice>(() => {
    const savedPrices = localStorage.getItem("obelisk_crypto_prices");
    if (savedPrices) {
      try {
        const parsed = JSON.parse(savedPrices);
        return { ...INITIAL_CURRENCIES, ...parsed };
      } catch (e) {
        return INITIAL_CURRENCIES;
      }
    }
    return INITIAL_CURRENCIES;
  });
  const [calculatedWithdrawal, setCalculatedWithdrawal] =
    useState<CalculatedWithdrawal | null>(null);
  const [lastUpdate, setLastUpdate] = useState<string>(() => {
    const savedUpdate = localStorage.getItem("obelisk_prices_last_update");
    return savedUpdate || "";
  });

  useEffect(() => {
    fetchCryptoPrices();
    dispatch(getTransactions({ type: "withdrawal" }) as any);
    dispatch(getTransactionStatistics() as any);

    if (user?.wallet_connected && !wallet) {
      dispatch(getWallet() as any);
    }

    const interval = setInterval(fetchCryptoPrices, 60000);
    return () => clearInterval(interval);
  }, [dispatch, user, wallet]);

  const savePricesToStorage = (prices: CryptoPrice, timestamp: number) => {
    try {
      localStorage.setItem("obelisk_crypto_prices", JSON.stringify(prices));
      localStorage.setItem(
        "obelisk_prices_last_update_timestamp",
        timestamp.toString(),
      );
      const updateTime = new Date(timestamp * 1000).toLocaleTimeString();
      setLastUpdate(updateTime);
      localStorage.setItem("obelisk_prices_last_update", updateTime);
    } catch (error) {
      console.error("Error saving prices to storage:", error);
    }
  };

  const fetchCryptoPrices = async () => {
    setFetchingPrices(true);
    try {
      const response = await transactionService.getCryptoPrices();

      if (response.success && response.prices) {
        const mergedPrices = { ...cryptoPrices };
        let hasNewData = false;

        Object.entries(response.prices).forEach(([currency, data]) => {
          const priceData = data as any;
          if (
            priceData &&
            typeof priceData === "object" &&
            typeof priceData.price === "number" &&
            priceData.price > 0
          ) {
            mergedPrices[currency] = priceData;
            hasNewData = true;
          }
        });

        if (hasNewData) {
          setCryptoPrices(mergedPrices);
          savePricesToStorage(
            mergedPrices,
            response.timestamp || Math.floor(Date.now() / 1000),
          );
          toast.success("Prices updated successfully");
        }
      } else {
        toast.success("Using cached prices");
        const savedTimestamp = localStorage.getItem(
          "obelisk_prices_last_update_timestamp",
        );
        if (savedTimestamp) {
          const timestamp = parseInt(savedTimestamp);
          const updateTime = new Date(timestamp * 1000).toLocaleTimeString();
          setLastUpdate(updateTime);
        }
      }
    } catch (error: any) {
      console.error("Price fetch error:", error);
      const savedTimestamp = localStorage.getItem(
        "obelisk_prices_last_update_timestamp",
      );
      if (savedTimestamp) {
        const timestamp = parseInt(savedTimestamp);
        const updateTime = new Date(timestamp * 1000).toLocaleTimeString();
        setLastUpdate(updateTime);
        toast.error("Network error - Using cached prices");
      } else {
        toast.error("Failed to fetch prices - Please try again");
      }
    } finally {
      setFetchingPrices(false);
    }
  };

  const handleCalculate = async () => {
    if (!formData.usd_amount || parseFloat(formData.usd_amount) <= 0) {
      toast.error("Please enter a valid USD amount");
      return;
    }

    const currentPrice = cryptoPrices[formData.currency]?.price || 0;
    if (currentPrice === 0) {
      toast.error("Please wait for prices to load");
      return;
    }

    const usdAmount = parseFloat(formData.usd_amount);
    const cryptoAmount = usdAmount / currentPrice;
    const minWithdrawalUsd = 20;
    const meetsMinimum = usdAmount >= minWithdrawalUsd;

    if (user && usdAmount > user.balance) {
      toast.error("Insufficient balance");
      return;
    }

    const network =
      cryptoPrices[formData.currency]?.network ||
      INITIAL_CURRENCIES[formData.currency as keyof typeof INITIAL_CURRENCIES]
        ?.network ||
      "Unknown Network";

    const localCalculation: CalculatedWithdrawal = {
      usd_amount: usdAmount,
      crypto_amount: cryptoAmount,
      currency: formData.currency,
      crypto_price: currentPrice,
      network: network,
      meets_minimum: meetsMinimum,
      minimum_required: minWithdrawalUsd,
    };

    if (!meetsMinimum) {
      toast.error(
        `Minimum withdrawal is ${formatFiat(minWithdrawalUsd)}. Your amount is ${formatFiat(usdAmount)}.`,
      );
      setCalculatedWithdrawal(localCalculation);
      return;
    }

    setCalculating(true);
    try {
      setCalculatedWithdrawal(localCalculation);
    } catch (error) {
      console.error("Calculation error:", error);
    } finally {
      setCalculating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.usd_amount || parseFloat(formData.usd_amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    if (!calculatedWithdrawal) {
      toast.error("Please calculate the amount first");
      return;
    }

    if (!calculatedWithdrawal.meets_minimum) {
      toast.error(
        `Minimum withdrawal is ${formatFiat(calculatedWithdrawal.minimum_required)}`,
      );
      return;
    }

    setLoading(true);
    try {
      await dispatch(
        withdraw({
          usd_amount: parseFloat(formData.usd_amount),
          currency: formData.currency,
          current_price: calculatedWithdrawal.crypto_price,
        }) as any,
      );

      toast.success("Withdrawal submitted successfully!");
      setFormData({ usd_amount: "", currency: "BTC" });
      setCalculatedWithdrawal(null);
      dispatch(getTransactions({ type: "withdrawal" }) as any);
      dispatch(getTransactionStatistics() as any);
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to submit withdrawal");
    } finally {
      setLoading(false);
    }
  };

  const handleMaxAmount = () => {
    if (user) {
      setFormData({ ...formData, usd_amount: user.balance.toString() });
    }
  };

  const recentWithdrawals = transactions
    .filter((t) => t.type === "withdrawal")
    .slice(0, 3);

  const getCurrentPrice = (currency: string) => {
    return cryptoPrices[currency]?.price || 0;
  };

  const formatPriceDisplay = (price: number, currency: string) => {
    if (price === 0) {
      return fetchingPrices ? "Loading..." : "—";
    }

    if (price > 1000) {
      return `$${price.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    } else if (price > 1) {
      return `$${price.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      })}`;
    } else {
      return `$${price.toLocaleString(undefined, {
        minimumFractionDigits: 6,
        maximumFractionDigits: 6,
      })}`;
    }
  };

  const getLastUpdateDisplay = () => {
    if (fetchingPrices) return "Updating...";
    if (lastUpdate) return `Last updated: ${lastUpdate}`;
    const savedUpdate = localStorage.getItem("obelisk_prices_last_update");
    if (savedUpdate) {
      setLastUpdate(savedUpdate);
      return `Last updated: ${savedUpdate}`;
    }
    return "Never updated";
  };

  if (!wallet && !walletLoading && (!user || !user.wallet_connected)) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5 p-6 border border-primary/10">
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-primary/20 to-transparent rounded-full -translate-y-32 translate-x-32"></div>
          <div className="relative">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10">
                <ArrowUpRight className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Withdraw Funds
                </h1>
                <p className="text-gray-600 dark:text-gray-400">
                  Withdraw cryptocurrency to your connected wallet
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Wallet Not Connected Card */}
        <div className={`${GlobalStyles.card}`}>
          <div className="text-center">
            <div className="mx-auto w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
              <WalletIcon className="h-10 w-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              Wallet Not Connected
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Connect your wallet to make withdrawals
            </p>

            <div
              className={`${GlobalStyles.alert} ${GlobalStyles.alertWarning} mb-6 max-w-md mx-auto`}
            >
              <AlertTriangle className="h-5 w-5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium">Withdrawal Security</p>
                <p className="text-sm mt-1">
                  Withdrawals can only be sent to your connected wallet address
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/wallet"
                className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} group/connect`}
              >
                <WalletIcon className="h-5 w-5 mr-2 transition-transform group-hover/connect:-translate-x-0.5" />
                <span>Connect Wallet</span>
              </Link>
              <Link
                to="/deposit"
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} group/deposit`}
              >
                <ArrowUpRight className="h-5 w-5 mr-2 rotate-180 transition-transform group-hover/deposit:-translate-y-0.5" />
                <span>Make Deposit First</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (walletLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="relative">
          <div className="absolute -inset-4 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-3xl blur-xl opacity-70"></div>
          <div
            className={`${GlobalStyles.spinner} h-10 w-10 border-3 relative`}
          ></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5 p-6 border border-primary/10">
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-primary/20 to-transparent rounded-full -translate-y-32 translate-x-32"></div>
        <div className="relative">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10">
                <ArrowUpRight className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Withdraw Funds
                </h1>
                <p className="text-gray-600 dark:text-gray-400">
                  Withdraw cryptocurrency using live market prices
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center px-3 py-1.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800 rounded-lg">
                <WalletIcon className="h-4 w-4 mr-2" />
                <span className="text-sm font-medium">Wallet Connected</span>
              </div>
              <button
                onClick={fetchCryptoPrices}
                disabled={fetchingPrices}
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} disabled:opacity-50 group/refresh`}
              >
                {fetchingPrices ? (
                  <div className="flex items-center">
                    <div
                      className={`${GlobalStyles.spinner} w-4 h-4 mr-2 border-2`}
                    ></div>
                    Updating...
                  </div>
                ) : (
                  <div className="flex items-center">
                    <RefreshCw className="h-4 w-4 mr-2 transition-transform group-hover/refresh:rotate-180" />
                    Refresh Prices
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left Column - Withdrawal Form */}
        <div className="lg:col-span-2 space-y-4">
          {/* Price Ticker */}
          <div className={`${GlobalStyles.card}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Crypto Prices
              </h3>
              <div className="flex items-center gap-2 text-sm">
                <TrendingUp
                  className={`h-4 w-4 ${fetchingPrices ? "text-amber-500" : "text-emerald-500"}`}
                />
                <span
                  className={`${fetchingPrices ? "text-amber-500" : "text-gray-500 dark:text-gray-400"}`}
                >
                  {getLastUpdateDisplay()}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {Object.keys(INITIAL_CURRENCIES).map((currency) => {
                const priceData = cryptoPrices[currency];
                const price = priceData?.price || 0;
                const network =
                  priceData?.network ||
                  INITIAL_CURRENCIES[
                    currency as keyof typeof INITIAL_CURRENCIES
                  ]?.network ||
                  "Unknown";

                return (
                  <div
                    key={currency}
                    className={`p-2 rounded-lg text-center transition-all duration-200 group/ticker ${
                      fetchingPrices
                        ? "bg-gray-100 dark:bg-gray-800 animate-pulse"
                        : price === 0
                          ? "bg-gray-100 dark:bg-gray-800"
                          : "bg-gray-50 dark:bg-gray-800/50 hover:bg-primary/5"
                    }`}
                  >
                    <div className="font-semibold text-gray-900 dark:text-white mb-1 group-hover/ticker:text-primary transition-colors">
                      {currency}
                    </div>
                    <div
                      className={`text-xs font-medium ${
                        fetchingPrices
                          ? "text-gray-400 dark:text-gray-500"
                          : price === 0
                            ? "text-gray-500 dark:text-gray-400"
                            : "text-emerald-500"
                      }`}
                    >
                      {formatPriceDisplay(price, currency)}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                      {network}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Withdrawal Form */}
          <div className={`${GlobalStyles.card}`}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Withdrawal Details
              </h3>
              <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                <WalletIcon className="h-4 w-4 mr-1" />
                Balance: {formatFiat(user?.balance || 0)}
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* USD Amount */}
              <div className="group">
                <div className="flex items-center justify-between mb-1">
                  <label
                    className={`${GlobalStyles.inputLabel} flex items-center gap-1`}
                  >
                    <span>USD Amount</span>
                    <Sparkles className="h-3 w-3 text-primary" />
                  </label>
                  <button
                    type="button"
                    onClick={handleMaxAmount}
                    className="text-sm text-primary hover:text-primary/80 transition-colors"
                  >
                    Use Max
                  </button>
                </div>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    value={formData.usd_amount}
                    onChange={(e) =>
                      setFormData({ ...formData, usd_amount: e.target.value })
                    }
                    className={`${GlobalStyles.input} pl-10`}
                    placeholder="0.00"
                    required
                    min="0.01"
                  />
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                    <WalletIcon className="h-4 w-4 text-primary" />
                  </div>
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <span className="text-gray-700 dark:text-gray-300 font-medium">
                      USD
                    </span>
                  </div>
                </div>
                <div className="flex justify-between mt-1">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Your balance: {formatFiat(user?.balance || 0)}
                  </p>
                  {user && formData.usd_amount && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Remaining:{" "}
                      {formatFiat(
                        user.balance - parseFloat(formData.usd_amount),
                      )}
                    </p>
                  )}
                </div>
              </div>

              {/* Currency Selection */}
              <div className="group">
                <label
                  className={`${GlobalStyles.inputLabel} flex items-center gap-1`}
                >
                  <span>Cryptocurrency</span>
                  <Sparkles className="h-3 w-3 text-primary" />
                </label>
                <select
                  value={formData.currency}
                  onChange={(e) =>
                    setFormData({ ...formData, currency: e.target.value })
                  }
                  className={GlobalStyles.input}
                  disabled={fetchingPrices}
                >
                  {Object.keys(INITIAL_CURRENCIES).map((currency) => (
                    <option key={currency} value={currency}>
                      {currency}
                    </option>
                  ))}
                </select>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Current price:{" "}
                  {formatPriceDisplay(
                    getCurrentPrice(formData.currency),
                    formData.currency,
                  )}
                </p>
              </div>

              {/* Calculate Button */}
              <div>
                <button
                  type="button"
                  onClick={handleCalculate}
                  disabled={
                    calculating ||
                    !formData.usd_amount ||
                    fetchingPrices ||
                    getCurrentPrice(formData.currency) === 0
                  }
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} w-full group/calc disabled:opacity-50`}
                >
                  {calculating ? (
                    <div className="flex items-center justify-center">
                      <div
                        className={`${GlobalStyles.spinner} w-4 h-4 mr-2 border-2`}
                      ></div>
                      Calculating...
                    </div>
                  ) : fetchingPrices ? (
                    <div className="flex items-center justify-center">
                      <div
                        className={`${GlobalStyles.spinner} w-4 h-4 mr-2 border-2`}
                      ></div>
                      Loading Prices...
                    </div>
                  ) : getCurrentPrice(formData.currency) === 0 ? (
                    <div className="flex items-center justify-center">
                      <div
                        className={`${GlobalStyles.spinner} w-4 h-4 mr-2 border-2`}
                      ></div>
                      Waiting for Price...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <Calculator className="h-4 w-4 mr-2 transition-transform group-hover/calc:scale-110" />
                      Calculate Amount
                    </div>
                  )}
                </button>
              </div>

              {/* Calculated Withdrawal Display */}
              {calculatedWithdrawal && (
                <div
                  className={`p-4 border rounded-xl backdrop-blur-sm ${
                    calculatedWithdrawal.meets_minimum
                      ? "bg-emerald-500/10 border-emerald-200 dark:border-emerald-800"
                      : "bg-amber-500/10 border-amber-200 dark:border-amber-800"
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4
                      className={`font-medium ${calculatedWithdrawal.meets_minimum ? "text-emerald-800 dark:text-emerald-300" : "text-amber-800 dark:text-amber-300"}`}
                    >
                      Withdrawal Summary
                    </h4>
                    {calculatedWithdrawal.meets_minimum ? (
                      <CheckCircle className="h-5 w-5 text-emerald-500" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-amber-500" />
                    )}
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        USD Amount:
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {formatFiat(calculatedWithdrawal.usd_amount)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Exchange Rate:
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        1 {calculatedWithdrawal.currency} = $
                        {calculatedWithdrawal.crypto_price.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Network:
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {calculatedWithdrawal.network}
                      </span>
                    </div>
                    <div className="pt-2 border-t border-current">
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          You Will Receive:
                        </span>
                        <span
                          className={`text-lg font-bold ${calculatedWithdrawal.meets_minimum ? "text-emerald-500" : "text-amber-500"}`}
                        >
                          {formatCrypto(
                            calculatedWithdrawal.crypto_amount,
                            calculatedWithdrawal.currency,
                          )}
                        </span>
                      </div>
                    </div>
                    {!calculatedWithdrawal.meets_minimum && (
                      <div className="mt-2 p-2 bg-rose-500/10 border border-rose-200 dark:border-rose-800 rounded">
                        <p className="text-sm text-rose-600 dark:text-rose-400 text-center">
                          Minimum withdrawal:{" "}
                          {formatFiat(calculatedWithdrawal.minimum_required)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div>
                <button
                  type="submit"
                  disabled={
                    loading ||
                    !calculatedWithdrawal ||
                    fetchingPrices ||
                    !calculatedWithdrawal?.meets_minimum
                  }
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} w-full group/submit disabled:opacity-50`}
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div
                        className={`${GlobalStyles.spinner} w-4 h-4 mr-2 border-2`}
                      ></div>
                      Processing...
                    </div>
                  ) : fetchingPrices ? (
                    <div className="flex items-center justify-center">
                      <div
                        className={`${GlobalStyles.spinner} w-4 h-4 mr-2 border-2`}
                      ></div>
                      Loading...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <ArrowUpRight className="h-4 w-4 mr-2 transition-transform group-hover/submit:-translate-y-0.5" />
                      <span>Confirm Withdrawal</span>
                    </div>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Right Column - Info & Stats */}
        <div className="space-y-4">
          {/* Important Info */}
          <div className={`${GlobalStyles.card}`}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Important Information
            </h3>
            <div className="space-y-2">
              <div className="flex items-start">
                <CheckCircle className="h-4 w-4 text-emerald-500 mr-2 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Minimum withdrawal: $20 USD
                </p>
              </div>
              <div className="flex items-start">
                <CheckCircle className="h-4 w-4 text-emerald-500 mr-2 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Withdrawals use live prices
                </p>
              </div>
              <div className="flex items-start">
                <CheckCircle className="h-4 w-4 text-emerald-500 mr-2 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  All withdrawals require confirmation
                </p>
              </div>
              <div className="flex items-start">
                <CheckCircle className="h-4 w-4 text-emerald-500 mr-2 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Funds will be sent to your connected wallet
                </p>
              </div>
            </div>
          </div>

          {/* Warning */}
          <div className={`${GlobalStyles.alert} ${GlobalStyles.alertWarning}`}>
            <AlertTriangle className="h-5 w-5 flex-shrink-0" />
            <p className="text-sm">
              Withdrawals are processed manually by admin. Please allow 1-2
              hours for processing.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Withdrawal;
